import {
  Product as ElasticsearchProduct,
  ProductSearchResponse,
} from "reedy-data/api";

import { FacetResult, SearchProduct, SearchResult } from "~/hooks/search/types";

const DEFAULT_SEARCH_RESULT: SearchResult = {
  hits: [],
  found: 0,
  page: 1,
  perPage: 10,
  facets: undefined,
};

const ES_FIELD_TO_FACET: Record<string, string> = {
  publishers: "publisher",
};

function mapAggregationsToFacets(
  aggregations?: ProductSearchResponse["aggregations"],
): FacetResult[] | undefined {
  if (!aggregations?.length) return undefined;

  return aggregations.map((agg) => ({
    field: ES_FIELD_TO_FACET[agg.field] ?? agg.field,
    counts: agg.buckets.map((bucket) => ({
      value: bucket.key,
      count: bucket.doc_count,
      label: bucket.label ?? undefined,
      kind: bucket.kind ?? undefined,
      seq: bucket.seq ?? undefined,
      subjectCode: bucket.subject_code ?? undefined,
      subjectLabel: bucket.subject_label ?? undefined,
    })),
  }));
}

const toDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) return null;
  return date instanceof Date ? date : new Date(date);
};

export const mapElasticsearchToSearchProduct = (
  esProduct: ElasticsearchProduct,
): SearchProduct => ({
  id: esProduct.id || esProduct.identifier,
  isbn: esProduct.identifier,
  workId: esProduct.workId ?? undefined,
  title: esProduct.title,
  author: esProduct.author || [],
  publisher: esProduct.publishers?.[0] || "",
  publishers: esProduct.publishers?.map((name) => ({ name, role: null })),
  imprints: esProduct.imprints,
  publishedAt: toDate(esProduct.publishedAt),
  productGroupDescription: esProduct.productGroupDescription || "",
  description: esProduct.description ?? undefined,
  keywords: esProduct.keywords ?? undefined,
  genre: esProduct.genre ?? undefined,
});

export const mapElasticsearchResponse = (
  response: ProductSearchResponse | undefined,
): SearchResult => {
  if (!response) {
    return { ...DEFAULT_SEARCH_RESULT, hits: [] };
  }

  const hits =
    response.hits?.map((hit) => ({
      product: mapElasticsearchToSearchProduct(hit.document),
      score: hit.score ?? 0,
      matched_queries: hit.matched_queries ?? [],
    })) ?? [];

  return {
    hits,
    found: response.found ?? 0,
    page: response.page ?? 1,
    perPage: response.per_page ?? 10,
    facets: mapAggregationsToFacets(response.aggregations),
    tookMs: response.took_ms,
    ageRangeAvailableBounds: response.age_range_available_bounds ?? null,
  };
};
